import type { PageDefinition } from '@/types/page'
import { ADMIN_STATUS, DATA_SCOPE_OPTIONS, RESULT_STATUS, ROLE_STATUS, toOptions } from './common'

const ADMIN_ROLE_OPTIONS = [
  { value: 'super_admin', label: '超级管理员' },
  { value: 'operations_manager', label: '运营管理员' },
  { value: 'health_advisor', label: '健康顾问' },
  { value: 'drug_librarian', label: '药品库管理员' },
  { value: 'drug_reviewer', label: '药事审核员' },
  { value: 'support_agent', label: '客服专员' },
  { value: 'device_ops', label: '设备运维' },
  { value: 'analyst', label: '数据分析员' },
  { value: 'auditor', label: '审计员' },
]

export const adminUsersPage: PageDefinition = {
  id: 'admin-users',
  route: '#/settings/admins',
  title: '管理员与组织',
  purpose: '管理管理员账号、角色、数据范围、登录安全与强制下线，所有变更写入审计',
  group: '系统设置',
  icon: 'shield',
  kind: 'table',
  permission: 'admins.view',
  permissions: { manage: 'admins.manage', reset: 'admins.reset', force: 'admins.force_logout' },
  searchKeys: ['admin_no', 'name', 'account', 'phone', 'email', 'role_names', 'data_scope_label'],
  statusKey: 'status',
  statusMap: ADMIN_STATUS,
  timeField: 'last_login_at',
  summary: [
    { key: 'total', label: '管理员总数', kind: 'count' },
    { key: 'active', label: '启用账号', kind: 'count', where: { status: 'active' } },
    { key: 'mfa', label: '已开启双因素', kind: 'count', where: { mfa_enabled: true } },
    { key: 'roles', label: '角色类型', kind: 'distinct', field: 'role_names', suffix: ' 类' },
  ],
  columns: [
    { key: 'name', label: '管理员', type: 'primary', subtitleKey: 'account', minWidth: 160, sortable: true },
    { key: 'role_names', label: '角色', type: 'tag', minWidth: 150 },
    { key: 'data_scope_label', label: '数据范围', minWidth: 170 },
    { key: 'region_scope', label: '区域范围', minWidth: 150 },
    { key: 'mfa_enabled', label: '双因素', type: 'boolean', width: 76, align: 'center' },
    { key: 'status', label: '状态', type: 'status', width: 80 },
    { key: 'last_login_at', label: '最近登录', type: 'datetime', width: 150, sortable: true },
    { key: 'created_at', label: '创建时间', type: 'datetime', width: 150 },
  ],
  filters: [
    { key: 'keyword', label: '关键词', type: 'keyword', placeholder: '姓名、账号或手机号', width: 230 },
    { key: 'role_code', label: '角色', type: 'select', options: ADMIN_ROLE_OPTIONS, width: 130 },
    { key: 'status', label: '状态', type: 'select', options: toOptions(ADMIN_STATUS), width: 100 },
    { key: 'data_scope', label: '数据范围', type: 'select', options: DATA_SCOPE_OPTIONS, width: 150 },
    { key: 'last_login_at', label: '最近登录', type: 'daterange', timeField: 'last_login_at', width: 230 },
  ],
  formFields: [
    { key: 'admin_no', label: '管理员编号', type: 'readonly', readonlyOnEdit: true, help: '保存后由系统生成，不可修改', placeholder: '保存后自动生成' },
    { key: 'name', label: '姓名', type: 'text', required: true, maxLength: 20 },
    { key: 'account', label: '登录账号', type: 'text', required: true, pattern: '^[a-z][a-z0-9_]{3,31}$', patternMessage: '使用4-32位小写字母、数字或下划线', readonlyOnEdit: true },
    { key: 'phone', label: '手机号', type: 'phone', required: true, pattern: '^1[3-9]\\d{9}$', patternMessage: '请输入11位有效手机号' },
    { key: 'email', label: '邮箱', type: 'email', required: true, pattern: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$', patternMessage: '请输入有效邮箱' },
    { key: 'role_codes', label: '角色', type: 'multiselect', options: ADMIN_ROLE_OPTIONS, required: true, help: '权限取角色并集，数据范围取最小交集' },
    { key: 'data_scope', label: '数据范围', type: 'select', options: DATA_SCOPE_OPTIONS, required: true },
    { key: 'region_scope', label: '区域范围', type: 'text', required: true, maxLength: 40, placeholder: '如：上海市 / 浦东新区' },
    { key: 'mfa_enabled', label: '双因素认证', type: 'switch', defaultValue: true, help: '正式环境要求高风险角色必须开启' },
    { key: 'status', label: '账号状态', type: 'select', options: toOptions(ADMIN_STATUS), defaultValue: 'active' },
  ],
  createLabel: '新增管理员',
  createPermission: 'admins.manage',
  editPermission: 'admins.manage',
  exportScope: 'none',
  extraActions: [
    { key: 'roles', label: '角色权限', icon: 'lock', kind: 'link', to: '#/settings/roles', permission: 'roles.view' },
    { key: 'audit', label: '操作日志', icon: 'audit', kind: 'link', to: '#/settings/audit-logs', permission: 'audit.view' },
  ],
  rowActions: [
    { key: 'view', label: '详情' },
    { key: 'edit', label: '编辑', permission: 'admins.manage', tone: 'primary' },
    { key: 'reset-password', label: '重置密码', permission: 'admins.reset', confirm: true, confirmText: '重置后临时密码只展示一次，并要求管理员下次登录修改。' },
    { key: 'force-logout', label: '强制下线', permission: 'admins.force_logout', tone: 'danger', confirm: true },
    { key: 'toggle', label: '停用', permission: 'admins.manage', tone: 'danger', confirm: true, visibleWhen: { field: 'status', in: ['active'] } },
    { key: 'toggle', label: '启用', permission: 'admins.manage', visibleWhen: { field: 'status', in: ['disabled'] } },
  ],
  detailSections: [
    { title: '账号资料', fields: ['admin_no', 'name', 'account', 'phone', 'email', 'role_names', 'data_scope_label', 'region_scope', 'status'] },
    { title: '安全信息', fields: ['mfa_enabled', 'last_login_at', 'created_at', 'updated_at'] },
  ],
  notice: '超级管理员账号不支持被其他管理员停用；重置密码、强制下线和权限变更都会写入操作日志。',
  emptyHint: '当前筛选下没有管理员账号。',
  seedKey: 'admins',
}

export const rolesPage: PageDefinition = {
  id: 'roles',
  route: '#/settings/roles',
  title: '角色权限',
  purpose: '配置角色权限、数据范围与成员，预览权限变更影响并避免越权授权',
  group: '系统设置',
  icon: 'lock',
  kind: 'tree-table',
  permission: 'roles.view',
  permissions: { manage: 'roles.manage' },
  searchKeys: ['name', 'code', 'description', 'data_scope_label'],
  statusKey: 'status',
  statusMap: ROLE_STATUS,
  timeField: 'updated_at',
  summary: [
    { key: 'total', label: '角色总数', kind: 'count' },
    { key: 'builtin', label: '内置角色', kind: 'count', where: { is_builtin: true } },
    { key: 'custom', label: '自定义角色', kind: 'count', where: { is_builtin: false } },
    { key: 'members', label: '成员数', kind: 'sum', field: 'member_count', suffix: ' 人' },
  ],
  columns: [
    { key: 'name', label: '角色名称', type: 'primary', subtitleKey: 'code', minWidth: 180, sortable: true },
    { key: 'data_scope_label', label: '数据范围', minWidth: 180 },
    { key: 'permission_count', label: '权限数', type: 'number', width: 84, align: 'center' },
    { key: 'member_count', label: '成员数', type: 'number', width: 80, align: 'center' },
    { key: 'is_builtin', label: '类型', type: 'boolean', width: 76, align: 'center' },
    { key: 'status', label: '状态', type: 'status', width: 80 },
    { key: 'updated_at', label: '更新时间', type: 'datetime', width: 150 },
  ],
  filters: [
    { key: 'keyword', label: '关键词', type: 'keyword', placeholder: '角色名称、编码或说明', width: 230 },
    { key: 'data_scope', label: '数据范围', type: 'select', options: DATA_SCOPE_OPTIONS, width: 150 },
    { key: 'status', label: '状态', type: 'select', options: toOptions(ROLE_STATUS), width: 100 },
  ],
  formFields: [
    { key: 'name', label: '角色名称', type: 'text', required: true, maxLength: 24 },
    { key: 'code', label: '角色编码', type: 'text', required: true, pattern: '^[a-z][a-z0-9_]{2,31}$', patternMessage: '使用3-32位小写字母、数字或下划线', readonlyOnEdit: true },
    { key: 'description', label: '角色说明', type: 'textarea', full: true, maxLength: 200 },
    { key: 'data_scope', label: '数据范围', type: 'select', options: DATA_SCOPE_OPTIONS, required: true },
    { key: 'status', label: '状态', type: 'select', options: toOptions(ROLE_STATUS), defaultValue: 'active' },
  ],
  createLabel: '新增角色',
  createPermission: 'roles.manage',
  editPermission: 'roles.manage',
  exportScope: 'none',
  extraActions: [
    { key: 'admins', label: '管理员', icon: 'users', kind: 'link', to: '#/settings/admins', permission: 'admins.view' },
    { key: 'sync-permissions', label: '同步权限目录', icon: 'refresh', kind: 'action', permission: 'roles.manage', confirmText: '从模块与路由注册表同步权限目录，不会自动授予任何角色新权限。' },
  ],
  rowActions: [
    { key: 'view', label: '详情' },
    { key: 'permissions', label: '权限配置', permission: 'roles.manage', tone: 'primary' },
    { key: 'edit', label: '编辑', permission: 'roles.manage' },
    { key: 'members', label: '查看成员' },
    { key: 'toggle', label: '停用', permission: 'roles.manage', tone: 'danger', confirm: true, visibleWhen: { field: 'status', in: ['active'] } },
    { key: 'toggle', label: '启用', permission: 'roles.manage', visibleWhen: { field: 'status', in: ['disabled'] } },
  ],
  detailSections: [
    { title: '角色信息', fields: ['name', 'code', 'description', 'data_scope_label', 'member_count', 'permission_count', 'status'] },
    { title: '权限影响', fields: ['is_builtin', 'created_at', 'updated_at'] },
  ],
  notice: '内置角色不可删除或修改编码；修改权限会影响该角色下全部管理员，保存前必须预览影响范围。',
  emptyHint: '还没有配置角色。',
  seedKey: 'roles',
}

export const auditLogsPage: PageDefinition = {
  id: 'audit-logs',
  route: '#/settings/audit-logs',
  title: '操作日志',
  purpose: '只读追溯管理员、系统与任务的关键操作、请求编号与前后差异，不提供编辑和删除',
  group: '系统设置',
  icon: 'audit',
  kind: 'table',
  permission: 'audit.view',
  permissions: { export: 'audit.export' },
  readOnly: true,
  hideSelection: true,
  searchKeys: ['actor_name', 'actor_account', 'module', 'action', 'resource_label', 'request_no', 'ip', 'detail'],
  statusKey: 'result',
  statusMap: RESULT_STATUS,
  timeField: 'created_at',
  summary: [
    { key: 'total', label: '日志总数', kind: 'count' },
    { key: 'success', label: '成功操作', kind: 'count', where: { result: '成功' } },
    { key: 'failed', label: '失败操作', kind: 'count', where: { result: '失败' } },
    { key: 'actors', label: '操作管理员', kind: 'distinct', field: 'actor_name', suffix: ' 人' },
  ],
  columns: [
    { key: 'created_at', label: '操作时间', type: 'datetime', width: 150, sortable: true },
    { key: 'actor_name', label: '操作人', type: 'primary', subtitleKey: 'actor_account', width: 130 },
    { key: 'module', label: '业务模块', width: 100 },
    { key: 'action', label: '操作', width: 92 },
    { key: 'resource_label', label: '操作对象', minWidth: 190 },
    { key: 'result', label: '结果', type: 'status', width: 78 },
    { key: 'ip', label: 'IP 地址', width: 120 },
    { key: 'request_no', label: '请求编号', width: 160 },
  ],
  filters: [
    { key: 'keyword', label: '关键词', type: 'keyword', placeholder: '操作人、对象或请求编号', width: 240 },
    { key: 'module', label: '业务模块', type: 'select', options: ['用户管理', '家庭档案', '用药计划', '健康预警', '药品资料', '消息通知', '会员订单', '系统设置', '权限管理'].map(value => ({ value, label: value })), width: 116 },
    { key: 'action', label: '操作类型', type: 'select', options: ['新增', '编辑', '停用', '启用', '导出', '审核通过', '驳回', '关闭', '重置密码'].map(value => ({ value, label: value })), width: 112 },
    { key: 'result', label: '结果', type: 'select', options: toOptions(RESULT_STATUS), width: 90 },
    { key: 'created_at', label: '操作时间', type: 'daterange', timeField: 'created_at', width: 230 },
  ],
  exportPermission: 'audit.export',
  exportScope: 'filtered',
  rowActions: [
    { key: 'view', label: '查看差异' },
  ],
  detailSections: [
    { title: '操作记录', fields: ['created_at', 'actor_name', 'actor_account', 'module', 'action', 'resource_label', 'result'] },
    { title: '请求信息', fields: ['ip', 'request_no', 'detail', 'before_json', 'after_json'] },
  ],
  notice: '操作日志只读且不可删除；导出按当前筛选与权限裁剪，导出行为本身也会写入日志。',
  emptyHint: '当前筛选下没有操作日志。',
  seedKey: 'audit_logs',
}

export const generalSettingsPage: PageDefinition = {
  id: 'general-settings',
  route: '#/settings/general',
  title: '系统参数',
  purpose: '配置平台信息、品牌素材、服务时间、安全策略与原型开发模式，并说明当前原型版本',
  group: '系统设置',
  icon: 'settings',
  kind: 'form',
  permission: 'settings.view',
  permissions: { manage: 'settings.manage' },
  searchKeys: [],
  columns: [],
  filters: [],
  formFields: [],
  exportScope: 'none',
  readOnly: false,
  seedKey: 'settings',
  notice: '正式开发尚未开始；本页只保存当前浏览器原型内的配置，不连接数据库或正式服务。',
}

export const dashboardPage: PageDefinition = {
  id: 'dashboard',
  route: '#/dashboard',
  title: '工作台',
  purpose: '汇总用户活跃、用药执行、健康预警、家庭守护、设备与待办，形成运营总览',
  group: '仪表盘',
  icon: 'dashboard',
  kind: 'dashboard',
  permission: 'dashboard.view',
  permissions: {},
  searchKeys: [],
  columns: [],
  filters: [],
  summary: [],
  seedKey: 'users',
}

export const analyticsPage: PageDefinition = {
  id: 'analytics',
  route: '#/analytics',
  title: '经营分析',
  purpose: '分析用户活跃、用药依从、预警处理、设备在线、会员转化与客服 SLA',
  group: '数据分析',
  icon: 'chart',
  kind: 'dashboard',
  permission: 'analytics.view',
  permissions: { export: 'analytics.export' },
  searchKeys: [],
  columns: [],
  filters: [],
  summary: [],
  exportPermission: 'analytics.export',
  seedKey: 'orders',
}

export const operationsPage: PageDefinition = {
  id: 'operations',
  route: '#/settings/operations',
  title: '运维管理',
  purpose: '查看当前原型路由、交互回归、任务与错误注入状态，不展示未发生的生产运行事实',
  group: '系统设置',
  icon: 'monitor',
  kind: 'dashboard',
  permission: 'ops.view',
  permissions: {},
  searchKeys: [],
  columns: [],
  filters: [],
  readOnly: true,
  seedKey: 'async_tasks',
}

export const profilePage: PageDefinition = {
  id: 'profile',
  route: '#/profile',
  title: '个人中心',
  purpose: '维护当前管理员资料、安全设置、通知偏好、会话与角色视角预览',
  group: '基础页面',
  icon: 'user',
  kind: 'form',
  permission: 'profile.view',
  permissions: { edit: 'profile.edit' },
  searchKeys: [],
  columns: [],
  filters: [],
  formFields: [
    { key: 'name', label: '姓名', type: 'text', required: true, maxLength: 20 },
    { key: 'phone', label: '手机号', type: 'phone', required: true, pattern: '^1[3-9]\\d{9}$', patternMessage: '请输入11位有效手机号' },
    { key: 'email', label: '邮箱', type: 'email', required: true },
  ],
  seedKey: 'admins',
}

export const decorationPage: PageDefinition = {
  id: 'decoration',
  route: '#/decoration',
  title: '装修中心',
  purpose: '从用户端页面注册表选择任意页面，在真实预览与 DIY 画布间切换并保存装修配置',
  group: '运营中心',
  icon: 'decoration',
  kind: 'editor',
  permission: 'decoration.view',
  permissions: { manage: 'decoration.manage', save: 'decoration.save' },
  searchKeys: [],
  columns: [],
  filters: [],
  seedKey: 'decoration_pages',
}

function statePage(id: string, route: string, title: string, purpose: string, icon: string, permission = 'public'): PageDefinition {
  return {
    id,
    route,
    title,
    purpose,
    group: '基础页面',
    icon,
    kind: 'state',
    permission,
    permissions: {},
    searchKeys: [],
    columns: [],
    filters: [],
    readOnly: true,
    seedKey: 'users',
  }
}

export const loginPage = statePage('login', '#/login', '登录', '以平台管理员账号建立可审计会话，并按角色和数据范围进入工作台', 'login')
export const forbiddenPage = statePage('forbidden', '#/403', '无权访问', '说明当前角色无权访问并给出申请或返回路径', 'lock', 'authenticated')
export const notFoundPage = statePage('not-found', '#/404', '页面不存在', '处理未知路由和失效深链，提供搜索与返回入口', 'compass')
export const serverErrorPage = statePage('server-error', '#/500', '服务异常', '说明服务异常、提供请求编号、重试与联系支持', 'alert')
export const maintenancePage = statePage('maintenance', '#/maintenance', '系统维护', '在维护窗口说明影响范围、预计恢复时间与联系渠道', 'wrench')
export const offlinePage = statePage('offline', '#/offline', '网络已断开', '处理断网和弱网，保留当前上下文并提供恢复入口', 'wifi-off')
